import { DeleteOutlined } from "@ant-design/icons";
import { Col, Input, Radio, Row } from "antd";
import { FormattedMessage, useIntl } from "react-intl";

import { formatPlaceholder } from "../../lib/formats/common";
import { useLocalityOptions } from "../../lib/hooks/basic/localities";
import { useCurrentUser } from "../../lib/hooks/basic/users";
import { CostCenterSelect } from "../finance";
import { Form, NamePath, Rules, shouldUpdate } from "../form/Form";
import { SelectField } from "../form/SelectField";

const namePathArray = (name: NamePath): (string | number)[] =>
  Array.isArray(name) ? name : [name as string | number];

const primaryCol = {
  flex: "48px",
  style: {
    minWidth: 0,
    minHeight: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
} as const;

export function LocalityCostCenterListFormItem({
  name,
  required,
  withPrimary,
}: {
  name: NamePath;
  required?: boolean;
  withPrimary?: boolean;
}) {
  const intl = useIntl();
  const { currentTenant } = useCurrentUser();

  return (
    <Form.Item
      key="locality-cost-centers"
      noStyle
      shouldUpdate={shouldUpdate(name)}
    >
      {({ getFieldValue, setFieldValue }) => (
        <div style={{ marginBottom: 16 }}>
          <Row
            align="middle"
            wrap={false}
            gutter={[2, 0]}
            style={{ marginBottom: 2 }}
          >
            <Col flex="1 1 0" style={{ minWidth: 0 }}>
              <Form.Item
                label={<FormattedMessage id="localities" />}
                required={required}
                compact
              />
            </Col>
            {currentTenant.features.costCenters && (
              <Col flex="1 1 0" style={{ minWidth: 0 }}>
                <Form.Item
                  label={<FormattedMessage id="costCenters" />}
                  compact
                />
              </Col>
            )}
            {withPrimary && (
              <Col {...primaryCol}>
                <Form.Item
                  compact
                  label={
                    <FormattedMessage
                      id="machines.primaryLocalityHint"
                      defaultMessage="Primary Farm"
                    />
                  }
                />
              </Col>
            )}
            <Col flex="32px" />
          </Row>

          {!required && !getFieldValue(name).filter(Form.undestroyed).length ? (
            <Row align="middle" gutter={[2, 0]} style={{ marginBottom: 2 }}>
              <Col flex="1 1 0" style={{ minWidth: 0 }}>
                <Input
                  readOnly
                  value={intl.formatMessage({ id: "localities.all" })}
                />
              </Col>
              {currentTenant.features.costCenters && (
                <Col flex="1 1 0" style={{ minWidth: 0 }}>
                  <Form.Item name="costCenterId" compact>
                    <CostCenterSelect useDefault />
                  </Form.Item>
                </Col>
              )}
              {withPrimary && <Col {...primaryCol} />}
              <Col flex="32px" />
            </Row>
          ) : null}

          <Form.List
            name={name}
            hideRemoveLabel
            removeButtonProps={() => ({
              block: false,
              type: "text",
              icon: <DeleteOutlined />,
            })}
            addText={
              <FormattedMessage
                id="cropStages.addLocality"
                defaultMessage="Add Specific Farm"
              />
            }
            renderItem={({ key, name: index }, removeIcon) => (
              <Row
                key={key}
                align="top"
                wrap={false}
                gutter={2}
                style={{ marginBottom: 2 }}
              >
                <Col flex="1 1 0" style={{ minWidth: 0 }}>
                  <Form.Item
                    compact
                    name={[index, "localityId"]}
                    rules={[Rules.required]}
                  >
                    <SelectField
                      optionsHook={useLocalityOptions}
                      placeholder={formatPlaceholder({
                        id: "localities.entityName",
                      })}
                    />
                  </Form.Item>
                </Col>

                {currentTenant.features.costCenters && (
                  <Col flex="1 1 0" style={{ minWidth: 0 }}>
                    <Form.Item compact name={[index, "costCenterId"]}>
                      <CostCenterSelect useDefault />
                    </Form.Item>
                  </Col>
                )}

                {withPrimary && (
                  <Col {...primaryCol}>
                    <Form.Item
                      compact
                      name={[index, "primary"]}
                      valuePropName="checked"
                    >
                      <Radio
                        style={{ marginInlineEnd: 0 }}
                        disabled={
                          !getFieldValue(
                            namePathArray(name).concat(index, "localityId")
                          )
                        }
                        onChange={(e) => {
                          if (!e.target.checked) return;
                          const path = namePathArray(name);
                          const list = getFieldValue(path) || [];
                          list.forEach((_: any, i: number) => {
                            if (i !== index) {
                              setFieldValue([...path, i, "primary"], false);
                            }
                          });
                        }}
                      />
                    </Form.Item>
                  </Col>
                )}
                <Col flex="32px">{removeIcon}</Col>
              </Row>
            )}
          />
        </div>
      )}
    </Form.Item>
  );
}
